import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/layout/AdminLayout'
import AdminContentLayout from '../../components/layout/AdminContentLayout'
import AvatarPreview from '../../components/individual/AvatarPreview'
import ShopItemCard from '../../components/individual/ShopItemCard'
import ShopCart from '../../components/individual/ShopCart'
import { useAuthStore } from '../../store/useAuthStore'
import useRewardStore from '../../store/rewardStore'
import { apiRequest } from '../../utils/api'

const TABS = [
  { key: 'all',        label: 'All' },
  { key: 'character',  label: 'Characters' },
  { key: 'hat',        label: 'Hats' },
  { key: 'skin',       label: 'Clothes' },
  { key: 'hand_item',  label: 'Accessories' },
  { key: 'background', label: 'Backgrounds' },
]

const SECTION_LABELS = {
  character:  'Characters',
  skin:       'Clothes',
  hat:        'Hats',
  hand_item:  'Accessories',
  background: 'Backgrounds',
}

const CATEGORY_ORDER = ['character', 'hat', 'skin', 'hand_item', 'background']

const AvatarShop = () => {
  const navigate   = useNavigate()
  const user       = useAuthStore((state) => state.user)
  const { coins, gems, avatar, updateAvatar, updateBalance, fetchRewardSummary } = useRewardStore()

  // ── 로컬 상태 ──────────────────────────────────────────────
  const [shopItems,     setShopItems]     = useState([])
  const [previewAvatar, setPreviewAvatar] = useState({
    character: avatar.character || 'explorer',
    skin:       avatar.skin       || null,
    hat:        avatar.hat        || null,
    handItem:   avatar.handItem   || null,
    background: avatar.background || null,
  })
  const [cart,       setCart]      = useState([])
  const [activeTab,  setActiveTab] = useState('all')
  const [saving,     setSaving]    = useState(false)
  const [buying,     setBuying]    = useState(null)
  const [cartError,  setCartError] = useState('')
  const [loading,    setLoading]   = useState(true)

  // ── 데이터 로딩 ─────────────────────────────────────────────
  useEffect(() => {
    if (!user?.USER_ID) return
    Promise.all([
      loadShopItems(),
      fetchRewardSummary(user.USER_ID).catch(() => {}),
    ]).finally(() => setLoading(false))
  }, [user?.USER_ID])

  useEffect(() => {
    setPreviewAvatar({
      character: avatar.character || 'explorer',
      skin:       avatar.skin       || null,
      hat:        avatar.hat        || null,
      handItem:   avatar.handItem   || null,
      background: avatar.background || null,
    })
  }, [avatar.character, avatar.skin, avatar.hat, avatar.handItem, avatar.background])

  const loadShopItems = async () => {
    try {
      const res = await apiRequest('/shop/items', 'POST', { USER_ID: user.USER_ID })
      if (res.success === 'Y' && res.data?.ITEMS) {
        setShopItems(res.data.ITEMS.map(i => ({ ...i, is_purchased: !!i.is_purchased })))
      }
    } catch (_) {}
  }

  // ── 아이템 클릭 ─────────────────────────────────────────────
  const handleItemClick = useCallback((item) => {
    const owned = shopItems.find(i => i.id === item.id)?.is_purchased

    // 미리보기는 항상 반영
    setPreviewAvatar((prev) => {
      const slotKey = categoryToSlotKey(item.category)
      const current = prev[slotKey]
      if (current === item.item_key) {
        if (item.category === 'character') return prev
        return { ...prev, [slotKey]: null }
      }
      return { ...prev, [slotKey]: item.item_key }
    })

    // 미구매 아이템은 카트에도 추가
    if (!owned) {
      setCart((prev) => {
        if (prev.find(c => c.id === item.id)) return prev
        return [...prev, item]
      })
    }
  }, [shopItems])

  // ── 저장 (preview → DB) ─────────────────────────────────────
  const handleSave = async () => {
    if (!user?.USER_ID || saving) return
    setSaving(true)
    try {
      const slots = [
        { slotKey: 'character',  category: 'character',  value: previewAvatar.character },
        { slotKey: 'skin',       category: 'skin',       value: previewAvatar.skin },
        { slotKey: 'hat',        category: 'hat',        value: previewAvatar.hat },
        { slotKey: 'handItem',   category: 'hand_item',  value: previewAvatar.handItem },
        { slotKey: 'background', category: 'background', value: previewAvatar.background },
      ]

      for (const slot of slots) {
        const saved = avatar[slot.slotKey]
        if (slot.value === saved) continue

        if (slot.value) {
          await apiRequest('/avatar/equip', 'POST', {
            USER_ID: user.USER_ID,
            ITEM_KEY: slot.value,
            CATEGORY: slot.category,
          })
        } else {
          await apiRequest('/avatar/unequip', 'POST', {
            USER_ID: user.USER_ID,
            CATEGORY: slot.category,
          })
        }
      }
      updateAvatar({ ...previewAvatar })
    } catch (err) {
      console.error('avatar save 에러:', err)
    } finally {
      setSaving(false)
    }
  }

  // ── 초기화 ──────────────────────────────────────────────────
  const handleReset = () => {
    setPreviewAvatar({
      character: previewAvatar.character,
      skin:       null,
      hat:        null,
      handItem:   null,
      background: null,
    })
  }

  // ── 구매 ────────────────────────────────────────────────────
  const handleBuy = async (paymentMethod) => {
    if (!user?.USER_ID || buying || cart.length === 0) return
    setCartError('')

    const targetItems = paymentMethod === 'gem'
      ? cart.filter(i => i.category === 'skin')
      : cart.filter(i => i.category !== 'skin')

    if (targetItems.length === 0) return

    const total = paymentMethod === 'gem'
      ? targetItems.reduce((s, i) => s + (i.price_gem || 0), 0)
      : targetItems.reduce((s, i) => s + i.price_coin, 0)
    const balance = paymentMethod === 'coin' ? coins : gems

    if (balance < total) {
      setCartError(`Insufficient balance (need ${total - balance} more ${paymentMethod === 'coin' ? 'coins' : 'gems'})`)
      return
    }

    setBuying(paymentMethod)
    const purchased = []
    let lastBalance = balance

    for (const item of targetItems) {
      try {
        const res = await apiRequest('/shop/purchase', 'POST', {
          USER_ID: user.USER_ID,
          ITEM_KEY: item.item_key,
          PAYMENT_METHOD: paymentMethod,
        })
        if (res.success === 'Y') {
          purchased.push(item.id)
          lastBalance = res.data?.NEW_BALANCE ?? lastBalance
        } else {
          setCartError(res.message || 'Purchase failed')
          break
        }
      } catch (_) {
        setCartError('Purchase failed. Please try again.')
        break
      }
    }

    if (purchased.length > 0) {
      setShopItems((prev) =>
        prev.map(i => purchased.includes(i.id) ? { ...i, is_purchased: true } : i)
      )
      setCart((prev) => prev.filter(i => !purchased.includes(i.id)))
      updateBalance(
        paymentMethod === 'coin'
          ? { coins: lastBalance }
          : { gems: lastBalance }
      )
    }

    setBuying(null)
  }

  // ── 필터링 ──────────────────────────────────────────────────
  const filteredItems = activeTab === 'all'
    ? shopItems
    : shopItems.filter(i => i.category === activeTab)

  const isEquipped = (item) => previewAvatar[categoryToSlotKey(item.category)] === item.item_key

  // ────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <AdminContentLayout>
        <style>{`
          .shop-grid { display: grid; grid-template-columns: 280px 1fr 300px; gap: 20px; align-items: start; }
          .shop-items-grid { display: grid; grid-template-columns: repeat(6,1fr); gap: 16px 12px; }
          @media (max-width: 1200px) {
            .shop-grid { grid-template-columns: 240px 1fr 260px; }
            .shop-items-grid { grid-template-columns: repeat(5,1fr); }
          }
          @media (max-width: 1024px) {
            .shop-grid { grid-template-columns: 1fr; }
            .shop-items-grid { grid-template-columns: repeat(4,1fr); }
          }
          @media (max-width: 768px) {
            .shop-items-grid { grid-template-columns: repeat(3,1fr); }
          }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* 헤더 한 줄 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 18,
            gap: 16,
            flexWrap: 'wrap',
          }}>
            {/* 로고 + 타이틀 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg,#E8EDFF,#D4DDFF)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="/assets/icons/shop.png" alt="shop" style={{ width: 26, height: 26, objectFit: 'contain' }} /></div>
              <div>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: '#0D2A4F' }}>Avatar Shop</h1>
                <p style={{ margin: 0, fontSize: 11, color: '#93A3B8', fontWeight: 600 }}>Use Coins or Gems to get cool items!</p>
              </div>
            </div>

            {/* 탭 */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, justifyContent: 'center' }}>
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 999,
                    border: activeTab === tab.key ? 'none' : '1.5px solid #E7ECF3',
                    background: activeTab === tab.key ? 'linear-gradient(135deg,#6C8CFF,#4F6BED)' : '#F8FBFF',
                    color: activeTab === tab.key ? '#fff' : '#7A889B',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all .2s',
                    boxShadow: activeTab === tab.key ? '0 3px 10px rgba(79,107,237,.25)' : 'none',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 잔액 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BalanceChip src="/assets/icons/coin.png" value={coins} />
              <BalanceChip src="/assets/icons/gem.png" value={gems} />
            </div>
          </div>

          {/* 3열 레이아웃 */}
          <div className="shop-grid">

            {/* 좌: 아바타 미리보기 */}
            <div style={panelStyle}>
              <AvatarPreview
                previewAvatar={previewAvatar}
                onReset={handleReset}
                onSave={handleSave}
                saving={saving}
              />
            </div>

            {/* 중: 아이템 그리드 */}
            <div style={{ ...panelStyle, maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#B0BBC8' }}>Loading…</div>
              ) : activeTab === 'all' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                  {CATEGORY_ORDER.map(cat => {
                    const catItems = shopItems.filter(i => i.category === cat)
                    if (catItems.length === 0) return null
                    return (
                      <div key={cat}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0D2A4F' }}>{SECTION_LABELS[cat]}</p>
                          <button
                            onClick={() => setActiveTab(cat)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#4F6BED' }}
                          >
                            See all →
                          </button>
                        </div>
                        <div className="shop-items-grid">
                          {catItems.slice(0, 6).map(item => (
                            <ShopItemCard
                              key={item.id}
                              item={item}
                              isOwned={item.is_purchased}
                              isEquipped={isEquipped(item)}
                              isInCart={!!cart.find(c => c.id === item.id)}
                              onClick={handleItemClick}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="shop-items-grid">
                  {filteredItems.map(item => (
                    <ShopItemCard
                      key={item.id}
                      item={item}
                      isOwned={item.is_purchased}
                      isEquipped={isEquipped(item)}
                      isInCart={!!cart.find(c => c.id === item.id)}
                      onClick={handleItemClick}
                      previewCharacter={previewAvatar.character}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 우: 장바구니 */}
            <div style={panelStyle}>
              <ShopCart
                cart={cart}
                coins={coins}
                gems={gems}
                onRemove={(id) => { setCart(prev => prev.filter(i => i.id !== id)); setCartError('') }}
                onBuy={handleBuy}
                buying={buying}
                error={cartError}
                previewCharacter={previewAvatar.character}
              />
            </div>

          </div>
        </div>
      </AdminContentLayout>
    </AdminLayout>
  )
}

// ── 유틸 ────────────────────────────────────────────────────
const categoryToSlotKey = (category) => {
  const map = { character: 'character', skin: 'skin', hat: 'hat', hand_item: 'handItem', background: 'background' }
  return map[category] || category
}

const panelStyle = {
  background: '#ffffff',
  border: '1px solid #E7ECF3',
  borderRadius: 28,
  padding: 20,
  boxShadow: '0 8px 32px rgba(79,107,237,.08)',
}

const BalanceChip = ({ src, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F4F7FF', border: '1.5px solid #E7ECF3', borderRadius: 14, padding: '7px 14px' }}>
    <img src={src} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
    <span style={{ fontSize: 15, fontWeight: 800, color: '#0D2A4F' }}>{value?.toLocaleString() ?? 0}</span>
    <span style={{ fontSize: 13, fontWeight: 700, color: '#4F6BED', marginLeft: 2 }}>+</span>
  </div>
)

export default AvatarShop
